import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Role } from 'src/app/core/models/role.enum';
import { SelectItem } from 'src/app/core/models/responses/select-item';
import { ApplicationUserService } from 'src/app/core/services/application-user.service';
import { UiThemeService } from 'src/app/core/services/ui-theme.service';
import { ApplicationUser } from 'src/app/models/application-user/application-user';
import { Branch } from 'src/app/models/branch/branch';
import { AlertService } from 'src/app/services/alert.service';
import { DataService } from 'src/app/services/data.service';
import { CustomerCreateEditModalComponent } from '../../../components/customers/customer-create-edit-modal/customer-create-edit-modal.component';

interface QuickAction {
  label: string;
  icon: string;
  /** Rota ya da modal: ikisinden biri dolu olur. */
  link?: string;
  action?: 'customer-modal';
  /** Bu rolü olmayan kullanıcıda gösterilmez. */
  role?: Role;
}

/**
 * Modern kabuk üst barı.
 *
 * Davranışlar klasik topbar ile aynıdır (şube değiştirme, müşteri arama,
 * bildirim sesi, çıkış); yalnızca yerleşim ve "Yeni İşlem" menüsü yenidir.
 */
@Component({
  selector: 'app-modern-topbar',
  templateUrl: './modern-topbar.component.html',
  styleUrls: ['./modern-topbar.component.scss']
})
export class ModernTopbarComponent implements OnInit, OnDestroy {

  @Output() mobileMenuButtonClicked = new EventEmitter<void>();

  user: ApplicationUser;
  currentBranch: Branch;
  hasUserRole = false;
  model: SelectItem[];

  private subscriptions = new Subscription();
  private _playSound: boolean = localStorage.getItem('playSound') === 'true';

  /** Ekrandaki hızlı işlem kısayolları. Rotalar pages-routing üzerinden doğrulandı. */
  private readonly allQuickActions: QuickAction[] = [
    { label: 'Yeni Satış', icon: 'bx bx-cart', link: '/retail/fast-selling/operation', role: Role.Retail },
    { label: 'Yeni Alış / Satış', icon: 'bx bx-transfer-alt', link: '/exchange/exchange-operation', role: Role.Wholesale },
    { label: 'Yeni Ürün', icon: 'bx bx-package', link: '/retail/product/create', role: Role.Retail },
    { label: 'Yeni Müşteri', icon: 'bx bx-user-plus', action: 'customer-modal' },
    { label: 'Tahsilat (müşteri seç)', icon: 'bx bx-wallet', link: '/specifications/customer' }
  ];

  constructor(
    private router: Router,
    private applicationUserService: ApplicationUserService,
    private dataService: DataService,
    private modalService: NgbModal,
    private alertService: AlertService,
    private uiThemeService: UiThemeService
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.applicationUserService.currentUser.subscribe(user => {
        this.user = user;
        this.hasUserRole = user?.roles?.includes(Role.User) === true;
      })
    );

    this.subscriptions.add(
      this.applicationUserService.currentBranch.subscribe(branch => this.currentBranch = branch)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get playSound(): boolean {
    return this._playSound;
  }

  get isDark(): boolean {
    return this.uiThemeService.isDark;
  }

  toggleDarkMode(): void {
    this.uiThemeService.toggleMode();
  }

  get quickActions(): QuickAction[] {
    const roles = this.user?.roles || [];
    return this.allQuickActions.filter(action => !action.role || roles.includes(action.role));
  }

  toggleMobileMenu(event: Event): void {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => term
        ? this.dataService
            .getCustomer({ name: term, isActive: true, branchId: this.currentBranch?.id })
            .toPromise()
            .then(res => res.data)
        : [])
    )

  resultFormatCustomer(value: SelectItem) {
    return value.text;
  }

  inputFormatCustomer(value: SelectItem) {
    return value?.text ? value.text : value;
  }

  selectedCustomer(event: any): void {
    event.preventDefault();
    this.model = null;
    this.router.navigate(['/specifications/customer/detail/' + event.item.value]);
  }

  runQuickAction(action: QuickAction): void {
    if (action.action === 'customer-modal') {
      this.openCustomerModal();
      return;
    }

    this.router.navigateByUrl(action.link);
  }

  openCustomerModal(): void {
    this.modalService.open(CustomerCreateEditModalComponent);
  }

  changeBranch(branch: Branch): void {
    this.applicationUserService.changeBranch(branch);
    this.currentBranch = branch;
    this.router.navigateByUrl('/dashboard');
  }

  toggleSounds(): void {
    this._playSound = !this._playSound;
    localStorage.setItem('playSound', String(this._playSound));
    this.alertService.showSuccessToast('Sesler ' + (this._playSound ? 'açıldı' : 'kapatıldı'));
  }

  logout(): void {
    this.applicationUserService.logout();
    this.router.navigate(['/login']);
  }
}
